<template>
  <section v-if="showSplash" :class="['header', headerSize, headerBgColor]">
    <div class="header-box">
      <router-link class="img-container" to="/">
        <img alt="legend logo" class="logo" :src="headerLogo" />
      </router-link>
      <div class="menu" @mouseleave.stop="menuIndex = -1">
        <div
          v-for="(item, index) in menuList"
          :key="index"
          class="menu-item"
          :class="menuTextColor"
          @mouseenter.stop="menuEnter(item, index)"
          @click="menuClick(item, index)"
        >
          <span>{{ item.name }}</span>
          <svg-triangle
            v-if="item.child.length"
            class="icon-triangle"
            :class="item.showMenu ? 'open' : ''"
          />
          <div class="sub-menu-list" v-if="item.child.length && item.showMenu">
            <div
              v-for="(subItem, subIndex) in item.child"
              class="sub-menu"
              :key="subIndex"
              @click.stop="subMenuClick(subItem)"
            >
              <svg-logo v-if="subItem.icon === 'legend'" />
              <svg-group v-else-if="subItem.icon === 'partner'" />
              <svg-ring v-else-if="subItem.icon === 'support'" />
              <span>{{ subItem.name }}</span>
              <svg-arrow />
              <p v-if="subItem.desc">{{ subItem.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<i18n>
{
  "en": {
    "About": "About",
    "Contact Us": "Contact Us"
  }
}
</i18n>

<script>
import Vue from "vue";
import { mapState, mapActions } from "vuex";
import SvgLogo from "@/assets/logo.svg?inline";
import SvgGroup from "@/assets/img/common/icon-group.svg?inline";
import SvgRing from "@/assets/img/common/icon-ring.svg?inline";
import SvgTriangle from "@/assets/img/menu/triangle.svg?inline";
import SvgArrow from "@/assets/img/common/arrow.svg?inline";

export default Vue.extend({
  name: "CommonHeader",
  data() {
    return {
      showSubMenu: false,
      menuIndex: -1,
      menuList: [
        {
          name: "PRODUCTS",
          key: "products",
          href: "/",
          showMenu: false,
          child: [
            {
              name: "LEGEND OTC",
              key: "legend-otc"
            },
            {
              name: "LEGEND PAY",
              key: "legend-pay"
            },
            {
              name: "TRADE WITH US",
              key: "kyc"
            }
          ]
        },
        {
          name: "SHOWCASES",
          href: "/",
          key: "cases",
          showMenu: false,
          child: []
        },
        {
          name: "DEVELOPERS",
          href: "/",
          key: "developers",
          showMenu: false,
          child: []
        },
        {
          name: "COMPANY",
          href: "/",
          key: "company",
          showMenu: false,
          child: [
            {
              name: "ABOUT US",
              icon: "legend",
              desc:
                "We believe the future of global finance should be open to everyone.",
              key: "about"
            },
            {
              name: "BECOME A PARTNER",
              icon: "partner",
              key: "partner"
            },
            {
              name: "SUPPORT HELP CENTER",
              icon: "support",
              key: "https://support.legendtrading.com/hc/"
            }
          ]
        },
        {
          name: "CONTACT",
          href: "/",
          key: "contact",
          showMenu: false,
          child: []
        }
      ],
      headerClass: {
        dark: "dark-header",
        light: "light-header",
        transparent: ""
      }
    };
  },
  watch: {
    menuIndex(newValue, oldValue) {
      if (newValue === -1) {
        this.menuList[oldValue].showMenu = false;
      } else if (oldValue === -1) {
        this.menuList[newValue].showMenu = true;
      } else {
        this.menuList[oldValue].showMenu = false;
        this.menuList[newValue].showMenu = true;
      }
    }
  },
  components: {
    SvgLogo,
    SvgGroup,
    SvgRing,
    SvgTriangle,
    SvgArrow
  },
  computed: {
    ...mapState({
      open: state => state.isZendeskOpened
    }),
    showSplash() {
      return this.$route.meta.splash !== "hide";
    },
    header() {
      return this.$route.meta.header;
    },
    background() {
      return this.$route.meta.background;
    },
    isHeaderDark() {
      return this.header === "dark";
    },
    isHeaderDarkTransparent() {
      return ["dark", "transparent"].includes(this.header);
    },
    bindZendeskOnce() {
      return once(this.bindZendeskOnClose);
    },
    headerBgColor() {
      return this.headerClass[this.header];
    },
    headerSize() {
      return this.background === "grid-upper" ? "wide" : "";
    },
    headerLogo() {
      return this.isHeaderDarkTransparent
        ? require("../assets/img/menu/header-logo-white.svg")
        : require("../assets/img/menu/header-logo-black.svg");
    },
    menuTextColor() {
      return this.isHeaderDarkTransparent ? "dark-menu" : "light-menu";
    }
  },
  methods: {
    ...mapActions(["toggleZendesk"]),
    menuEnter(item, index) {
      if (item.child.length) {
        this.menuIndex = index;
      } else {
        this.menuIndex = -1;
      }
    },
    menuClick(item, index) {
      if (!item.child.length) {
        if (item.key === "contact") {
          this.toggleZendesk(!this.open);
        } else {
          this.$router.push({ name: item.key }, () => {});
        }
      }
    },
    subMenuClick({ key }) {
      this.menuIndex = -1;
      if (key.startsWith("https://")) {
        window.open(key);
      } else {
        this.$router.push({ name: key }, () => {});
      }
    }
  },
  beforeRouteEnter(to, from, next) {
    next();
  }
});
</script>

<style scoped lang="scss">
.header {
  width: 100%;
  height: 100%;
  font-size: 1rem;
  z-index: 10;
  &-box {
    height: 6.25rem;
    padding: 0 3.75rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 90rem;
    margin: 0 auto;
    background-color: transparent;
    position: relative;
    z-index: 2;
    justify-content: space-between;
    .logo {
      width: 14.4375rem;
      height: auto;
    }
    .menu {
      // flex: 1;
      margin-left: 2.625rem;
      display: flex;
      flex-direction: row;
      align-items: center;
      &-item {
        cursor: pointer;
        font-weight: 500;
        margin-bottom: -1rem;
        padding-bottom: 1rem;
        position: relative;
        text-align: center;
        width: 8.75rem;
        transition: color 0.2s ease-in-out;
        .icon-triangle {
          margin: 7px 0 0 0.5rem;
          transition: transform 0.2s ease-in-out;
          vertical-align: top;
          &.open {
            transform: rotate(-180deg);
          }
        }
        .sub-menu-list {
          background-color: #fff;
          border-radius: 1rem;
          box-shadow: 4px 10px 8px 0 rgba(17, 18, 36, 0.05);
          color: #111224;
          position: absolute;
          right: 0;
          width: max-content;
          text-align: left;
          margin: 1rem 0 0;
          padding: 1rem;
          .sub-menu {
            align-items: center;
            background-color: rgba(17, 18, 36, 0.02);
            border-radius: 0.625rem;
            display: flex;
            flex-flow: row wrap;
            font-size: 1rem;
            font-weight: 500;
            margin-bottom: 0.625rem;
            justify-content: space-between;
            padding: 1.25rem 0.25rem 1.25rem 1.25rem;
            transition: 0.2s ease-in-out;
            max-width: 18rem;
            &:last-child {
              margin-bottom: 0;
            }
            &:hover {
              background-color: rgba(17, 18, 36, 0.05);
              color: #c5ab80;
            }
            & > svg {
              color: #c5ab80;
              margin: 0 1rem;
              &:first-child {
                color: inherit;
                margin: 0 0.5rem 0 0;
                max-width: 1.25rem;
                max-height: 1.25rem;
              }
            }
            & > span {
              flex: 1;
            }
            & > p {
              font-size: 0.875rem;
              font-weight: 400;
              line-height: 1.4;
              margin: 0.5rem 0 0;
              opacity: 0.3;
            }
          }
          .sub-menu-nor {
            color: #ffffff;
          }
          .sub-menu-sel {
            color: #c5ab80;
          }
        }
      }
      &-icon {
        height: 1.875rem;
        margin-left: 2.1875rem;
        width: 1.875rem;
      }
    }
  }
  &.wide &-box {
    padding: 0 3.75rem 3.125rem;
    height: 9.375rem;
    .img-container {
      transform: translateY(0rem);
    }
    .menu {
      margin-left: 2.625rem;
    }
  }
}
.dark-header {
  background: #0a0a16;
}

.light-header {
  background: #ffffff;
}
.dark-menu {
  color: #b7baca;
  &:hover {
    color: #fff;
  }
}
.light-menu {
  color: #111224;
  &:hover {
    color: #0a0a16;
  }
}
</style>
